import { PageStatus } from '@/types/store';
import type { FunnelPageRelationship } from '../../../../logic';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../../types';
import { ManageRow } from './ManageRow';
import type { PageSlot } from './manageFunnelPages.logic';

interface ManageBranchSubtreeProps {
    slot: PageSlot;
    stepNumber: number;
    parentStepNumber: number;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    relationshipByPageId: Map<number, FunnelPageRelationship>;
    positionByPageId: Map<number, number>;
    onHide: (pageId: number) => void;
    onDuplicate: (pageId: number) => void;
    onDelete: (page: FunnelPageItem) => void;
    isAncestorHidden?: boolean;
    newlyAddedPageId?: number | null;
}

export const ManageBranchSubtree = ({
    slot,
    stepNumber,
    parentStepNumber,
    productInfoByPageId,
    relationshipByPageId,
    positionByPageId,
    onHide,
    onDuplicate,
    onDelete,
    isAncestorHidden = false,
    newlyAddedPageId = null,
}: ManageBranchSubtreeProps) => {
    const { page } = slot;
    const nested = (slot.children ?? []).filter((c): c is PageSlot => c.kind === 'page');
    const isHidden = page.status === PageStatus.DRAFT;
    const childrenAncestorHidden = isAncestorHidden || isHidden;

    return (
        <div className='flex flex-col gap-1.5'>
            <ManageRow
                page={page}
                stepNumber={stepNumber}
                parentStepNumber={parentStepNumber}
                productInfo={productInfoByPageId[page.page_id]}
                relationship={relationshipByPageId.get(page.page_id)}
                isNested
                hideDragHandle
                isAncestorHidden={isAncestorHidden}
                isNew={page.page_id === newlyAddedPageId}
                onHide={() => onHide(page.page_id)}
                onDuplicate={() => onDuplicate(page.page_id)}
                onDelete={() => onDelete(page)}
            />

            {nested.length > 0 && (
                <div className='border-border-subtle ml-3 flex flex-col gap-1.5 border-l pl-3'>
                    {nested.map(nestedSlot => (
                        <ManageBranchSubtree
                            key={nestedSlot.page.page_id}
                            slot={nestedSlot}
                            stepNumber={positionByPageId.get(nestedSlot.page.page_id) ?? 0}
                            parentStepNumber={stepNumber}
                            productInfoByPageId={productInfoByPageId}
                            relationshipByPageId={relationshipByPageId}
                            positionByPageId={positionByPageId}
                            onHide={onHide}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                            isAncestorHidden={childrenAncestorHidden}
                            newlyAddedPageId={newlyAddedPageId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
